<template>
  <div>
    <vue-html2pdf
      ref="html2Pdf"
      class="my-export"
      :show-layout="false"
      :preview-modal="true"
      :enable-download="false"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      filename="doc"
      pdf-format="a4"
      pdf-content-width="auto"
      :html-to-pdf-options="htmlToPdfOptions"
      @progress="assignLodingTime($event)"
      @hasDownloaded="hasDownloaded"
    >
      <section
        id="my-pdf"
        ref="pdfContent"
        slot="pdf-content"
        :style="pdfContentStyle"
      ></section>
    </vue-html2pdf>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { eventBus } from "@/main";
import { mapActions } from "vuex";
import VueHtml2pdf from "vue-html2pdf";
import Loading from "@/components/Loading";

export default {
  name: "pdfExport",
  components: {
    VueHtml2pdf,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      isUpload: false,
      patientId: null,
      remark: "",
      userInteractionContext: null, // For iPad compatibility

      // Enhanced PDF options
      htmlToPdfOptions: {
        pagebreak: {
          mode: [],
          before: ".page-break-before",
          avoid: ".avoid-break",
        },
        image: {
          type: "jpeg",
          quality: 0.98,
        },
        margin: [15, 5, 15, 5], // Adjusted margins for better layout
        html2canvas: {
          dpi: 300,
          scale: 4,
          letterRendering: true,
          useCORS: true,
          logging: true, // Helpful for debugging
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
      },
      loadingTime: 0,
      pdfContentStyle: {
        letterSpacing: "0.01rem",
        fontSize: "13px",
        margin: "0.5rem 3rem",
        lineHeight: "1.5",
      },
    };
  },

  methods: {
    ...mapActions({
      createMediaFiles: "moduleMediaFile/createMediaFiles",
    }),

    async generatePdf({ header, body, footer, option }) {
      try {
        this.isLoading = true;
        this.isUpload = option?.isUpload || false;
        this.patientId = option?.patientId || null;
        this.remarkTitle = option?.remarkTitle || "";
        this.htmlToPdfOptions.jsPDF.orientation =
          option?.orientation || "portrait";

        // Apply custom styles for better page breaks
        if (option?.fontSize) {
          this.pdfContentStyle.fontSize = option.fontSize;
        }

        // Enhanced settings for signature mode to match print quality
        if (option?.isUpload) {
          // Higher quality settings for documents that will be signed
          this.htmlToPdfOptions.html2canvas.dpi = 400; // Increased from 300
          this.htmlToPdfOptions.html2canvas.scale = 6; // Increased from 4
          this.htmlToPdfOptions.image.quality = 1.0; // Maximum quality
        } else {
          // Reset to default for regular print
          this.htmlToPdfOptions.html2canvas.dpi = 300;
          this.htmlToPdfOptions.html2canvas.scale = 4;
          this.htmlToPdfOptions.image.quality = 0.98;
        }

        const pdfContent = this.$refs.pdfContent;
        pdfContent.innerHTML = ""; // Clear previous content

        // Add page break classes
        if (header) {
          const headerElement = header.cloneNode(true);
          headerElement.classList.add("avoid-break");
          await pdfContent.appendChild(headerElement);
        }

        if (body) {
          const bodyElement = body.cloneNode(true);
          // Add page break classes to sections if needed
          const sections = bodyElement.querySelectorAll(".section");
          sections.forEach(section => {
            section.classList.add("avoid-break");
          });
          await pdfContent.appendChild(bodyElement);
        }

        if (footer) {
          const footerElement = footer.cloneNode(true);
          footerElement.classList.add("avoid-break");
          await pdfContent.appendChild(footerElement);
        }

        await this.$nextTick();
        const data = await this.$refs.html2Pdf.generatePdf();
        return data;
      } catch (error) {
        console.error("PDF generation error:", error);
        this.isLoading = false;
        throw error;
      }
    },

    assignLodingTime(e) {
      this.loadingTime = e;
    },
    async hasDownloaded(pdfBlob) {
      this.resetLodingTime();
      if (!this.isUpload) return;
      // await this.handleCreateMediaFile(pdfBlob);
      // eventBus.$emit("pollForUpdates", [pdfBlob]);
      // Create URL from blob
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const filename = `${
        this.remarkTitle
      }${this.$utils.generateDateTimeString()}_Signed`;

      // Build the full URL for SignDoc
      const params = new URLSearchParams({
        src: pdfUrl,
        id: this.patientId,
        patientId: this.patientId,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        filename: filename,
      }).toString();
      
      const signDocUrl = `/document?${params}`;
      
      // iPad-friendly approach: Try multiple methods to open new tab
      this.openNewTabIpadFriendly(signDocUrl);
      this.$emit("openSignTabCompleted");
    },
    resetLodingTime() {
      this.isLoading = false;
      setTimeout(() => {
        this.loadingTime = 0;
      }, 1000);
    },
    
    openNewTabIpadFriendly(url) {
      // Detect if we're on iOS/iPad
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      if (isIOS) {
        // For iOS: Try to open immediately, but with fallback if blocked
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        
        if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
          // Popup was blocked, show user-friendly dialog to manually open
          this.showIpadSignDialog(url);
        }
      } else {
        // For non-iOS devices, use regular window.open
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    
    showIpadSignDialog(url) {
      // Emit event to parent component to show a proper modal dialog
      this.$emit('showSignDialog', {
        url: url,
        message: 'เอกสารพร้อมสำหรับเซ็นต์แล้ว',
        title: 'เปิดหน้าเซ็นต์เอกสาร'
      });
    },
    async handleCreateMediaFile(pdfBlob) {
      await this.createMediaFiles({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        data: {
          category: "Document",
          patientId: this.patientId ? String(this.patientId) : null,
          remark: `${
            this.remarkTitle
          }${this.$utils.generateDateTimeString()}_Signed`,
          filesMetadata: [{ id: null, contentType: "application/pdf" }],
        },
        files: [pdfBlob],
      });
    },
  },
  mounted() {
    // Apply styles to container
    const containerEl = document.querySelector(
      ".vue-html2pdf.my-export .layout-container .content-wrapper"
    );
    if (containerEl) {
      containerEl.style.setProperty("margin", "0", "important");
      containerEl.style.setProperty("background-color", "white", "important");
    }

    // Add global styles for PDF generation
    const styleEl = document.createElement("style");
    styleEl.textContent = `
      .avoid-break {
        page-break-inside: avoid !important;
        -webkit-page-break-inside: avoid !important;
      }
      .page-break-before {
        page-break-before: always !important;
        -webkit-page-break-before: always !important;
      }
      #my-pdf {
        font-family: 'Sarabun', sans-serif;
      }
      #my-pdf section {
        margin-bottom: 20px;
      }
    `;
    document.head.appendChild(styleEl);
  },

  beforeDestroy() {
    eventBus.$off("generatePDF");
  },
};
</script>

<style>
#my-pdf {
  font-size: 13px;
  margin: 0.5rem 3rem;
}

/* Additional styles for better page breaks */
.avoid-break {
  page-break-inside: avoid;
  -webkit-page-break-inside: avoid;
}

.page-break-before {
  page-break-before: always;
  -webkit-page-break-before: always;
}
</style>
